import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { StyleSheet, TextInput, View } from "react-native";
import { Text } from "react-native-paper";

import type { QuestionComponentProps } from "./types";

import { useDraftRegistry } from "@/lib/DraftRegistry";
import { colors, fonts } from "@/lib/theme";

// Vital-sign range validation — warns on clinically unlikely values.
// Wire format unchanged; kept verbatim from the prior NumberQuestion so the
// existing test fixtures + form-builder QIDs continue to fire.
const RANGE_RULES: Record<
    string,
    { min: number; max: number; warningMin: number; warningMax: number; unit: string }
> = {
    "191": { min: 90, max: 110, warningMin: 95, warningMax: 104, unit: "°F" },
    "195": { min: 20, max: 250, warningMin: 50, warningMax: 120, unit: "bpm" },
    "79": { min: 4, max: 60, warningMin: 10, warningMax: 30, unit: "/min" },
    "33": { min: 20, max: 600, warningMin: 60, warningMax: 300, unit: "mg/dL" },
    "159": { min: 50, max: 100, warningMin: 90, warningMax: 100, unit: "%" },
    "87": { min: 50, max: 500, warningMin: 80, warningMax: 400, unit: "lbs" },
    "58": { min: 0, max: 15, warningMin: 0.5, warningMax: 10, unit: "LPM" },
    "510": { min: 50, max: 100, warningMin: 90, warningMax: 100, unit: "%" },
    "90": { min: 500, max: 3000, warningMin: 1000, warningMax: 2000, unit: "mL/day" },
    "174": { min: 0, max: 30, warningMin: 0, warningMax: 3, unit: "episodes" },
    "181": { min: 0, max: 10, warningMin: 0, warningMax: 10, unit: "falls" },
    "199": { min: 1, max: 10, warningMin: 1, warningMax: 10, unit: "" },
    "6": { min: 0, max: 10, warningMin: 0, warningMax: 10, unit: "" }
};

/**
 * Numeric input — flat-section v2 visual.
 *
 * 26px ink-bold value with the unit in muted text. Two-tier severity from
 * the same RANGE_RULES the previous NumberQuestion enforced:
 *   - outside Possible → red value, red-tinted chip below with alert icon
 *   - outside Normal but inside Possible → amber value, amber-tinted chip
 *   - inside Normal → no chip
 *
 * No +/- steppers (those were a previous experiment; v2 drops them).
 */
export function NumberQuestion({
    question,
    value,
    onChange,
    readOnly,
    onFocus,
    onBlur
}: QuestionComponentProps) {
    // Local edit buffer. We do NOT call `onChange` on every keystroke — only
    // on blur and on unmount. This prevents follow-up branching from popping
    // in/out as the RN types a multi-digit number (e.g. typing "103" should
    // not briefly trigger a fever follow-up at "1" or "10" along the way).
    const [draft, setDraft] = useState<string>(value ?? "");

    // Re-sync the draft when the external `value` prop changes from outside
    // (e.g. server hydration on section load). Only sync when the field is
    // not being actively edited so we don't clobber the user's keystrokes.
    const editingRef = useRef(false);
    useEffect(() => {
        if (!editingRef.current) {
            setDraft(value ?? "");
        }
    }, [value]);

    // Unmount-flush. If the user navigates away with an unblurred draft,
    // push it to `onChange` so it isn't lost. The mutable stateRef lets the
    // cleanup see the LATEST draft / value / onChange — a naked `[]`-deps
    // cleanup would capture stale mount-time refs.
    const stateRef = useRef({ draft, value, onChange });
    stateRef.current = { draft, value, onChange };
    useEffect(() => {
        return () => {
            const { draft: d, value: v, onChange: cb } = stateRef.current;
            if (d !== (v ?? "")) {
                cb(d);
            }
        };
    }, []);

    // Register with the draft registry so the section's Complete handler can
    // synchronously flush this field's pending draft before running validation.
    // Without this, tapping Complete while the field is still focused would
    // validate against pre-blur state and falsely flag the field as empty.
    // The flush goes through stateRef so it always sees the latest values
    // (registration happens once at mount; the captured closure stays valid).
    const registry = useDraftRegistry();
    const questionIdStr = String(question.id);
    useEffect(() => {
        registry.register(questionIdStr, {
            flushIfDirty: () => {
                const { draft: d, value: v, onChange: cb } = stateRef.current;
                if (d !== (v ?? "")) {
                    cb(d);
                }
            }
        });
        return () => registry.unregister(questionIdStr);
    }, [registry, questionIdStr]);

    const rule = RANGE_RULES[String(question.id)];

    const { warning, outsidePossible } = useMemo(() => {
        if (!rule || !draft) {
            return { warning: null as string | null, outsidePossible: false };
        }
        const num = parseFloat(draft);
        if (isNaN(num)) {
            return { warning: null as string | null, outsidePossible: false };
        }
        if (num < rule.min || num > rule.max) {
            return {
                warning: `Value ${num} ${rule.unit} is outside the possible range (${rule.min}–${rule.max}). Please verify.`,
                outsidePossible: true
            };
        }
        if (num < rule.warningMin) {
            return {
                warning: `Value ${num} ${rule.unit} is critically low. Normal range is ${rule.warningMin}–${rule.warningMax}.`,
                outsidePossible: false
            };
        }
        if (num > rule.warningMax) {
            return {
                warning: `Value ${num} ${rule.unit} is critically high. Normal range is ${rule.warningMin}–${rule.warningMax}.`,
                outsidePossible: false
            };
        }
        return { warning: null as string | null, outsidePossible: false };
    }, [rule, draft]);

    const flagged = warning != null;
    const valueColor = flagged
        ? outsidePossible
            ? colors.error500
            : colors.warning500
        : colors.ink;
    const borderColor = flagged ? colors.warning500 : colors.border;
    const bgColor = flagged ? colors.warnBg : colors.surface;

    return (
        <View>
            <View style={[styles.field, { borderColor, backgroundColor: bgColor }]}>
                <TextInput
                    value={draft}
                    onChangeText={(v) => {
                        editingRef.current = true;
                        setDraft(v);
                    }}
                    editable={!readOnly}
                    placeholder="—"
                    placeholderTextColor={colors.textTertiary}
                    keyboardType="decimal-pad"
                    inputMode="decimal"
                    onFocus={() => {
                        editingRef.current = true;
                        onFocus?.();
                    }}
                    onBlur={() => {
                        editingRef.current = false;
                        if (draft !== (value ?? "")) {
                            onChange(draft);
                        }
                        onBlur?.();
                    }}
                    style={[styles.input, { color: valueColor }]}
                />
                <Text style={styles.unit}>{rule?.unit ?? question.hintText ?? ""}</Text>
            </View>
            {warning ? (
                <View
                    style={[
                        styles.chip,
                        outsidePossible
                            ? { backgroundColor: colors.errBg, borderColor: colors.error500 }
                            : { backgroundColor: colors.warnBg, borderColor: colors.warning500 }
                    ]}
                >
                    <MaterialCommunityIcons
                        name="alert-outline"
                        size={14}
                        color={outsidePossible ? colors.error500 : colors.warning500}
                    />
                    <Text style={styles.chipText}>{warning}</Text>
                </View>
            ) : null}
        </View>
    );
}

const styles = StyleSheet.create({
    field: {
        alignSelf: "flex-start",
        flexDirection: "row",
        alignItems: "baseline",
        gap: 6,
        paddingVertical: 10,
        paddingHorizontal: 14,
        borderWidth: 1.5,
        borderRadius: 12,
        minWidth: 140
    },
    input: {
        minWidth: 90,
        padding: 0,
        textAlign: "right",
        fontFamily: fonts.extraBold,
        fontSize: 26,
        letterSpacing: -0.6
    },
    unit: {
        fontFamily: fonts.bold,
        fontSize: 13,
        color: colors.textSecondary
    },
    chip: {
        marginTop: 10,
        paddingVertical: 9,
        paddingHorizontal: 12,
        borderRadius: 10,
        borderWidth: 1,
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 7
    },
    chipText: {
        flex: 1,
        fontFamily: fonts.semiBold,
        fontSize: 12,
        color: colors.ink,
        lineHeight: 17
    }
});
